import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { fmtMoney } from "../lib/api";
import { DemoLockNotice, DemoLockWrap } from "./DemoLock";

export const MIN_USD = 100_000;
export const MAX_SELF_USD = 500_000;
export const MAX_QUOTE_USD = 100_000_000;

/**
 * Acquisition-tier branching for the order ticket (used by ProjectDetail).
 *
 * Buckets the requested USD amount and renders the correct CTA(s):
 *   - tooLow      → minimum-acquisition notice
 *   - inSelfServe → wallet / Stripe Elements / SWIFT / USDT buttons
 *   - needsQuote  → "Request a quote" CTA
 *   - overCap     → hard-rejection notice
 */
export default function OrderActions({
  user, busy, tons, totalUsd, payCur, indexUsd, projectId, nav,
  onBuy, onCardElements, onHostedCard, onSwift, onUsdt, onQuote,
}) {
  const { t } = useTranslation();
  const tr = (k, o) => t(`pd.${k}`, o);
  if (!user) {
    return (
      <button
        onClick={() => nav("/login", { state: { from: `/project/${projectId}` } })}
        className="btn-primary w-full"
        data-testid="buy-login"
      >
        {tr("sign_in_to_trade")}
      </button>
    );
  }

  const cardEligible = payCur === "USD" || payCur === "EUR";
  const tooLow = totalUsd < MIN_USD;
  const needsQuote = totalUsd > MAX_SELF_USD && totalUsd <= MAX_QUOTE_USD;
  const overCap = totalUsd > MAX_QUOTE_USD;

  let body;
  if (overCap) {
    body = (
      <div className="border border-danger bg-danger/5 p-4 text-sm text-danger" data-testid="tier-over-cap">
        {tr("over_cap", { max: fmtMoney(MAX_QUOTE_USD, "USD") })}
      </div>
    );
  } else if (tooLow) {
    body = (
      <div className="border border-line bg-muted/40 p-4 space-y-2" data-testid="tier-min">
        <div className="label-tag">{tr("min_acquisition_title")}</div>
        <div className="text-sm text-ink">
          {tr("min_acquisition_desc_a")} <span className="mono-num font-serif">{fmtMoney(MIN_USD, "USD")}</span> {tr("min_acquisition_desc_b")}
        </div>
        <div className="text-xs text-ink2 mono-num">
          {tr("min_acquisition_status", { current: fmtMoney(totalUsd, "USD"), diff: fmtMoney(MIN_USD - totalUsd, "USD") })}
        </div>
        <div className="text-xs text-ink2 mono-num pt-1">
          {tr("min_acquisition_at_index", { tons: Math.ceil(MIN_USD / indexUsd).toLocaleString() })}
        </div>
      </div>
    );
  } else if (needsQuote) {
    body = (
      <>
        <div className="border border-ink bg-muted/30 p-3 text-xs font-mono text-ink2" data-testid="tier-quote-info">
          {tr("needs_quote_info", { max: fmtMoney(MAX_SELF_USD, "USD") })}
        </div>
        <button
          onClick={onQuote}
          disabled={tons <= 0}
          className="btn-primary w-full disabled:opacity-50"
          data-testid="buy-quote-btn"
        >
          {tr("request_quote_cta")}
        </button>
      </>
    );
  } else {
    body = (
      <>
        <button onClick={onBuy} disabled={busy || tons <= 0} className="btn-primary w-full disabled:opacity-50" data-testid="buy-confirm">
          {busy ? tr("processing") : tr("pay_wallet")}
        </button>
        <button
          onClick={() => {
            if (!cardEligible) { toast.error(tr("toast_card_usd_eur_only")); return; }
            onCardElements();
          }}
          disabled={busy || tons <= 0 || !cardEligible}
          className="btn-outline w-full disabled:opacity-50 mt-2"
          data-testid="buy-card-confirm"
          title={!cardEligible ? tr("switch_currency_hint") : ""}
        >
          {tr("pay_card_stripe")}
        </button>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <button onClick={onSwift} disabled={busy || tons <= 0} className="btn-outline disabled:opacity-50 !text-xs" data-testid="buy-swift-btn">
            {tr("swift_wire")}
          </button>
          <button onClick={onUsdt} disabled={busy || tons <= 0} className="btn-outline disabled:opacity-50 !text-xs" data-testid="buy-usdt-btn">
            {tr("usdt_label")}
          </button>
        </div>
        <button
          type="button"
          onClick={onHostedCard}
          disabled={busy || tons <= 0 || !cardEligible}
          className="block text-[10px] font-mono uppercase tracking-wider text-ink2 hover:text-ink mx-auto disabled:opacity-40 pt-1"
          data-testid="buy-card-hosted"
        >
          {tr("hosted_card")}
        </button>
      </>
    );
  }

  return (
    <>
      <DemoLockNotice action="buy" />
      <DemoLockWrap className="space-y-2">
        {body}
      </DemoLockWrap>
      <MitigateLink projectId={projectId} label={tr("retire_mitigate")} />
    </>
  );
}

function MitigateLink({ projectId, label }) {
  return (
    <Link
      to={`/mitigate?project=${projectId}`}
      className="block text-center text-xs font-mono tracking-wider text-ink2 underline pt-3 border-t border-line"
      data-testid="mitigate-link"
    >
      {label}
    </Link>
  );
}
